import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { Save, RefreshCw, AlertCircle, TrendingDown, ChevronDown, ChevronUp, FileText, RotateCcw } from 'lucide-react';

const C = {
  pageBg:    '#F7F6EF', white:     '#FFFFFF', rowAlt:    '#F7F6EF',
  ink:       '#2F3A42', mid:       '#7A8A96', faint:     '#A8B5BD',
  border:    '#D8DEE3', borderMid: '#C4CDD3',
  navy:      '#445366', navyLight: '#EAF4F8',
  green:     '#7EA594', greenBg:   '#EAF2EF',
  red:       '#C0424F', redBg:     '#FFF0F0',
  amber:     '#E07B00', amberBg:   '#FFF3E0',
  skyText:   '#3A8FA3', skyBg:     '#EAF4F8', cream:     '#F7F6EF',
};

const ENTITY = 'Box Plantage';
const ENTITY_STYLE = { bg: '#C8E6D8', text: '#2D7A65', border: '#7EA594' };

// Revenue-linked rows scale with revenue % change
const REVENUE_LINKED = new Set(['revenue', 'vat_net', 'cos', 'payroll']);

function getForecastMonths() {
  const now = new Date();
  const months = [];
  const year = now.getFullYear();
  for (let m = now.getMonth(); m <= 11; m++) {
    const d = new Date(year, m, 1);
    months.push({
      key: `${year}-${String(m + 1).padStart(2, '0')}`,
      label: d.toLocaleString('en-GB', { month: 'short' }),
      fullLabel: d.toLocaleString('en-GB', { month: 'long' }),
      year, month: m,
      daysInMonth: new Date(year, m + 1, 0).getDate(),
    });
  }
  return months;
}

const ROW_DEFS = [
  { key: 'revenue',     label: 'Revenue',              sign: 1,  spread: 'daily',           proRate: true  },
  { key: 'vat_net',     label: 'VAT net',               sign: 1,  spread: 'daily',           proRate: true  },
  { key: 'open_inv',    label: 'Open Invoices',         sign: -1, spread: 'daily_remaining', proRate: false },
  { key: 'cos',         label: 'COS',                   sign: -1, spread: 'daily',           proRate: true  },
  { key: 'other_exp',   label: 'Other Expenses',        sign: -1, spread: 'daily',           proRate: true  },
  { key: 'rent',        label: 'Rent',                  sign: -1, spread: 'lump', hitDay: 2,  proRate: true  },
  { key: 'payroll',     label: 'Payroll',               sign: -1, spread: 'lump', hitDay: 28, proRate: false },
  { key: 'covid_loan',  label: 'COVID/Loan repayments', sign: -1, spread: 'lump', hitDay: 29, proRate: false },
  { key: 'emp_dispute', label: 'Employee Dispute',      sign: -1, spread: 'lump', hitDay: 29, proRate: false },
  { key: 'vat_payment', label: 'VAT Payment',           sign: -1, spread: 'lump', hitDay: 29, proRate: false },
];

function daysRemaining(monthObj) {
  const now = new Date();
  const isCurrentMonth = now.getFullYear() === monthObj.year && now.getMonth() === monthObj.month;
  if (!isCurrentMonth) return monthObj.daysInMonth;
  return monthObj.daysInMonth - now.getDate();
}

function displayValue(fullVal, monthObj, shouldProRate = true) {
  const now = new Date();
  const isCurrentMonth = now.getFullYear() === monthObj.year && now.getMonth() === monthObj.month;
  if (!isCurrentMonth || !fullVal || !shouldProRate) return fullVal || 0;
  const remaining = daysRemaining(monthObj);
  return Math.round((fullVal / monthObj.daysInMonth) * remaining);
}

// Apply scenario overrides on top of base data
function applyScenario(baseData, months, revPct, overrides) {
  const factor = 1 + revPct / 100;
  const result = {};
  months.forEach(mObj => {
    const mData = { ...(baseData[mObj.key] || {}) };
    // Revenue scaling
    ROW_DEFS.forEach(row => {
      if (REVENUE_LINKED.has(row.key)) {
        const base = parseFloat(baseData[mObj.key]?.[row.key]) || 0;
        mData[row.key] = Math.round(base * factor);
      }
    });
    // Per-row overrides
    Object.entries(overrides).forEach(([rowKey, val]) => {
      if (val !== null && val !== '') mData[rowKey] = parseFloat(val) || 0;
    });
    result[mObj.key] = mData;
  });
  return result;
}

function fmt(n) {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('nl-NL').format(Math.abs(Math.round(n)));
}
function fmtEuro(n) {
  if (n == null || isNaN(n)) return '—';
  const abs = fmt(Math.abs(n));
  return n < 0 ? `(€${abs})` : `€${abs}`;
}
function fmtAxis(n) {
  if (Math.abs(n) >= 1000) return `€${(n / 1000).toFixed(0)}k`;
  return `€${Math.round(n)}`;
}

// ── Weekly chart engine ───────────────────────────────────────────────────────
function buildWeeklySeries(months, data, openingBalance) {
  const now = new Date();
  const year = now.getFullYear();

  const todayMon = new Date(now);
  const dow = todayMon.getDay();
  todayMon.setDate(todayMon.getDate() - (dow === 0 ? 6 : dow - 1));
  todayMon.setHours(0, 0, 0, 0);

  const endOfYear = new Date(year, 11, 31);
  const weeks = [];
  const cursor = new Date(todayMon);
  while (cursor <= endOfYear) { weeks.push(new Date(cursor)); cursor.setDate(cursor.getDate() + 7); }

  const monthMeta = {};
  months.forEach(mObj => {
    const mData = data[mObj.key] || {};
    const isCurrentMonth = now.getFullYear() === mObj.year && now.getMonth() === mObj.month;
    const remainingDays = isCurrentMonth ? Math.max(mObj.daysInMonth - now.getDate(), 1) : mObj.daysInMonth;
    const rates = {};
    ROW_DEFS.forEach(row => {
      const full = parseFloat(mData[row.key]) || 0;
      if (row.spread === 'daily') {
        rates[row.key] = { type: 'daily', daily: full / mObj.daysInMonth, full };
      } else if (row.spread === 'daily_remaining') {
        rates[row.key] = { type: 'daily', daily: full / remainingDays, full, fromDay: isCurrentMonth ? now.getDate() : 1 };
      } else if (row.spread === 'lump') {
        rates[row.key] = { type: 'lump', full, hitDay: row.hitDay };
      }
    });
    monthMeta[mObj.key] = rates;
  });

  const weekContainsDay = (mKey, day, weekStart, weekEnd) => {
    for (let d = new Date(weekStart); d <= weekEnd; d.setDate(d.getDate() + 1)) {
      const dMKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (dMKey === mKey && d.getDate() === day) return true;
    }
    return false;
  };

  let runningBalance = openingBalance || 0;
  const series = [];
  const fired = new Set();

  weeks.forEach(weekStart => {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);

    const segments = [];
    for (let d = new Date(weekStart); d <= weekEnd; d.setDate(d.getDate() + 1)) {
      const mKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const ex = segments.find(s => s.mKey === mKey);
      if (ex) { ex.days++; ex.dates.push(d.getDate()); }
      else segments.push({ mKey, days: 1, dates: [d.getDate()] });
    }

    let weekInflow = 0, weekOutflow = 0;

    segments.forEach(seg => {
      const rates = monthMeta[seg.mKey];
      if (!rates) return;
      ROW_DEFS.forEach(row => {
        const r = rates[row.key];
        if (!r) return;
        let contribution = 0;
        if (r.type === 'daily') {
          const validDays = r.fromDay ? seg.dates.filter(d => d >= r.fromDay).length : seg.days;
          contribution = r.daily * validDays;
        } else if (r.type === 'lump') {
          const k = `${seg.mKey}:${row.key}`;
          const [y, m] = seg.mKey.split('-').map(Number);
          const lastDay = new Date(y, m, 0).getDate();
          const effectiveDay = Math.min(r.hitDay, lastDay);
          if (!fired.has(k) && weekContainsDay(seg.mKey, effectiveDay, weekStart, weekEnd)) {
            fired.add(k);
            contribution = r.full;
          }
        }
        if (row.sign === 1) weekInflow += Math.abs(contribution);
        else weekOutflow += Math.abs(contribution);
      });
    });

    runningBalance += (weekInflow - weekOutflow);
    const isMonthStart = weekStart.getDate() <= 7;
    series.push({
      weekLabel: weekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
      balance: Math.round(runningBalance),
      inflow: Math.round(weekInflow), outflow: Math.round(weekOutflow),
      net: Math.round(weekInflow - weekOutflow),
      monthLabel: isMonthStart ? weekStart.toLocaleString('en-GB', { month: 'short' }) : null,
    });
  });

  return series;
}

// ── SVG Chart ─────────────────────────────────────────────────────────────────
function WeeklyChart({ series, forPrint = false }) {
  const [hovered, setHovered] = useState(null);
  const W = 920, H = forPrint ? 175 : 300;
  const padL = 68, padR = 24, padT = 28, padB = 52;
  const chartW = W - padL - padR, chartH = H - padT - padB;

  if (!series || series.length < 2) return null;

  const values = series.map(s => s.balance);
  const minV = Math.min(...values, 0), maxV = Math.max(...values, 0);
  const vRange = (maxV - minV) || 1;
  const vMin = minV - vRange * 0.15, vMax = maxV + vRange * 0.15, vSpan = vMax - vMin;

  const toX = i => padL + (i / (series.length - 1)) * chartW;
  const toY = v => padT + ((vMax - v) / vSpan) * chartH;
  const zeroY = toY(0);

  const rawStep = (vMax - vMin) / 5;
  const mag = Math.pow(10, Math.floor(Math.log10(Math.abs(rawStep) || 1)));
  const niceStep = Math.ceil(rawStep / mag) * mag;
  const yTicks = [];
  for (let v = Math.ceil(vMin / niceStep) * niceStep; v <= vMax + niceStep * 0.1; v += niceStep) yTicks.push(Math.round(v));

  const points = series.map((s, i) => ({ x: toX(i), y: toY(s.balance), ...s }));
  const linePath = `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`;

  // Filled areas clipped to zero line
  const buildAreaPath = (above) => {
    const segs = []; let seg = null;
    for (let i = 0; i < points.length; i++) {
      const p = points[i], inZ = above ? p.balance >= 0 : p.balance < 0;
      if (inZ) {
        if (!seg) {
          const prev = points[i-1];
          const ex = prev ? prev.x + ((0-prev.balance)/(p.balance-prev.balance))*(p.x-prev.x) : p.x;
          seg = [{x:ex,y:zeroY}, p];
        } else seg.push(p);
      } else if (seg) {
        const prev = points[i-1];
        const ex = prev ? prev.x + ((0-prev.balance)/(p.balance-prev.balance))*(p.x-prev.x) : prev.x;
        seg.push({x:ex, y:zeroY});
        segs.push([...seg]); seg = null;
      }
    }
    if (seg) { seg.push({x:points[points.length-1].x, y:zeroY}); segs.push(seg); }
    return segs.map(s => `M ${s.map(p => `${p.x},${p.y}`).join(' L ')} Z`).join(' ');
  };

  const monthBoundaries = series.map((s, i) => i > 0 && s.monthLabel ? { x: toX(i), label: s.monthLabel } : null).filter(Boolean);
  const showEvery = series.length > 20 ? 3 : series.length > 10 ? 2 : 1;

  return (
    <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', minWidth: 560, height: 'auto', display: 'block' }}
        onMouseLeave={() => !forPrint && setHovered(null)}>
        {monthBoundaries.map((b, i) => (
          <g key={i}>
            <line x1={b.x} x2={b.x} y1={padT} y2={padT + chartH} stroke={C.borderMid} strokeWidth={1} strokeDasharray="3 3" opacity={0.6} />
            <text x={b.x + 4} y={padT + 10} fontSize={9} fill={C.mid} fontWeight={700}>{b.label}</text>
          </g>
        ))}
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={padL} x2={W - padR} y1={toY(v)} y2={toY(v)} stroke={v === 0 ? C.borderMid : C.border} strokeWidth={v === 0 ? 1.5 : 1} strokeDasharray={v === 0 ? '4 3' : '2 4'} opacity={v === 0 ? 1 : 0.6} />
            <text x={padL - 6} y={toY(v) + 4} fontSize={9.5} fill={v === 0 ? C.ink : C.mid} textAnchor="end" fontWeight={v === 0 ? 700 : 400}>{fmtAxis(v)}</text>
          </g>
        ))}
        <path d={buildAreaPath(true)}  fill={C.green} opacity={0.25} />
        <path d={buildAreaPath(false)} fill={C.red}   opacity={0.25} />
        <line x1={padL} x2={W - padR} y1={zeroY} y2={zeroY} stroke={C.borderMid} strokeWidth={1.5} />
        <path d={linePath} fill="none" stroke={C.navy} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {/* Invisible hover targets — dot only appears on hover */}
        {!forPrint && points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={8} fill="transparent" style={{ cursor: 'pointer' }}
            onMouseEnter={() => setHovered(i)} />
        ))}
        {!forPrint && hovered !== null && (() => {
          const p = points[hovered];
          if (!p) return null;
          const tipW = 130, tipH = 56;
          const tx = Math.min(p.x - tipW / 2, W - padR - tipW);
          const ty = p.y - tipH - 10 < padT ? p.y + 12 : p.y - tipH - 10;
          return (
            <g>
              <circle cx={p.x} cy={p.y} r={4} fill={p.balance < 0 ? C.red : C.green} stroke={C.white} strokeWidth={1.5} />
              <rect x={tx} y={ty} width={tipW} height={tipH} rx={6} fill={C.white} stroke={C.border} strokeWidth={1} style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.10))' }} />
              <text x={tx + 8} y={ty + 16} fontSize={9} fontWeight={700} fill={C.mid}>{p.weekLabel}</text>
              <text x={tx + 8} y={ty + 31} fontSize={11} fontWeight={800} fill={p.balance < 0 ? C.red : C.green}>{fmtEuro(p.balance)}</text>
              <text x={tx + 8} y={ty + 46} fontSize={8.5} fill={C.mid}>{p.net >= 0 ? '+' : ''}{fmtAxis(p.net)} this week</text>
            </g>
          );
        })()}
        {points.map((p, i) => {
          if (i % showEvery !== 0) return null;
          return <text key={i} x={p.x} y={H - padB + 14} fontSize={8.5} fill={C.mid} textAnchor="middle" transform={`rotate(-40, ${p.x}, ${H - padB + 14})`}>{p.weekLabel}</text>;
        })}
      </svg>
    </div>
  );
}


// ── Scenario panel ────────────────────────────────────────────────────────────
function ScenarioPanel({ scenarioName, setScenarioName, revPct, setRevPct, overrides, setOverrides, onReset, onExport }) {
  const [expanded, setExpanded] = useState(true);

  const isActive = revPct !== 0 || Object.values(overrides).some(v => v !== null && v !== '');

  return (
    <div style={{ border: `1.5px solid ${isActive ? C.amber : C.border}`, borderRadius: 12, background: C.white, marginBottom: '1.5rem', overflow: 'hidden' }}>
      <button onClick={() => setExpanded(e => !e)} style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.75rem 1rem', background: isActive ? C.amberBg : C.rowAlt,
        border: 'none', cursor: 'pointer',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span style={{ fontSize: '0.78rem', fontWeight: 800, color: isActive ? C.amber : C.ink }}>
            Scenario Modelling {isActive && '· Active'}
          </span>
          {isActive && <span style={{ fontSize: '0.68rem', color: C.amber, background: C.amberBg, border: `1px solid ${C.amber}`, borderRadius: 4, padding: '1px 6px', fontWeight: 700 }}>Modified</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {isActive && (
            <div onClick={e => { e.stopPropagation(); onReset(); }} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', color: C.mid, cursor: 'pointer', padding: '2px 8px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.white }}>
              <RotateCcw size={11} /> Reset
            </div>
          )}
          <div onClick={e => { e.stopPropagation(); onExport(); }} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', color: C.white, cursor: 'pointer', padding: '4px 10px', borderRadius: 6, background: C.navy, fontWeight: 700 }}>
            <FileText size={11} /> Export PDF
          </div>
          {expanded ? <ChevronUp size={14} color={C.mid} /> : <ChevronDown size={14} color={C.mid} />}
        </div>
      </button>

      {expanded && (
        <div style={{ padding: '1rem', borderTop: `1px solid ${C.border}` }}>
          {/* Scenario name */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Scenario Name</div>
            <input
              value={scenarioName}
              onChange={e => setScenarioName(e.target.value)}
              placeholder="e.g. Base Case, No COVID Debt, Revenue +20%"
              style={{ width: '100%', border: `1px solid ${C.border}`, borderRadius: 7, padding: '0.45rem 0.75rem', fontSize: '0.85rem', fontWeight: 600, color: C.ink, background: C.white, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          {/* Revenue slider */}
          <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', background: C.rowAlt, borderRadius: 8, border: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: C.ink }}>Revenue adjustment</div>
              <div style={{ fontSize: '0.85rem', fontWeight: 900, color: revPct === 0 ? C.mid : revPct > 0 ? C.green : C.red }}>
                {revPct > 0 ? '+' : ''}{revPct}%
              </div>
            </div>
            <input type="range" min="-50" max="50" step="5" value={revPct}
              onChange={e => setRevPct(Number(e.target.value))}
              style={{ width: '100%', accentColor: C.navy, cursor: 'pointer' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: C.faint, marginTop: 2 }}>
              <span>−50%</span><span style={{ color: C.mid }}>Auto-scales revenue, VAT net & COS</span><span>+50%</span>
            </div>
          </div>

          {/* Per-row overrides */}
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.6rem' }}>Line item overrides (applied to all months)</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem' }}>
              {ROW_DEFS.filter(r => !REVENUE_LINKED.has(r.key)).map(row => {
                const val = overrides[row.key];
                const active = val !== null && val !== '';
                return (
                  <div key={row.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.6rem', borderRadius: 7, border: `1px solid ${active ? C.amber : C.border}`, background: active ? C.amberBg : C.white }}>
                    <div style={{ flex: 1, fontSize: '0.75rem', fontWeight: 600, color: C.ink }}>{row.label}</div>
                    <input
                      type="text"
                      placeholder="override"
                      value={val === null ? '' : val}
                      onChange={e => setOverrides(prev => ({ ...prev, [row.key]: e.target.value === '' ? null : e.target.value }))}
                      style={{ width: 80, border: `1px solid ${active ? C.amber : C.border}`, borderRadius: 5, padding: '3px 6px', fontSize: '0.75rem', textAlign: 'right', background: C.white, color: C.ink, outline: 'none' }}
                    />
                    {active && (
                      <button onClick={() => setOverrides(prev => ({ ...prev, [row.key]: null }))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.faint, fontSize: '0.8rem', padding: 0, lineHeight: 1 }}>✕</button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Number input ──────────────────────────────────────────────────────────────
function NumInput({ value, onChange }) {
  const [raw, setRaw] = useState('');
  const [focused, setFocused] = useState(false);
  const numVal = parseFloat(value) || 0;
  return (
    <input type="text"
      value={focused ? raw : (numVal === 0 ? '' : fmt(numVal))}
      placeholder="0"
      onFocus={() => { setRaw(numVal === 0 ? '' : String(numVal)); setFocused(true); }}
      onBlur={() => { const p = parseFloat(raw.replace(/[^0-9.-]/g, '')); onChange(isNaN(p) ? 0 : p); setFocused(false); }}
      onChange={e => setRaw(e.target.value)}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
      style={{ width: '100%', border: `1px solid ${focused ? C.navy : C.border}`, borderRadius: 6, padding: '0.35rem 0.5rem', fontSize: '0.8rem', fontWeight: 500, textAlign: 'right', background: C.white, color: C.ink, outline: 'none', boxSizing: 'border-box' }}
    />
  );
}

// ── Forecast input panel ──────────────────────────────────────────────────────
function ForecastInputPanel({ months, data, setCell }) {
  const [open, setOpen] = useState(null);
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.6rem' }}>Full Month Forecasts</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        {months.map(m => {
          const isOpen = open === m.key;
          const mData = data[m.key] || {};
          const hasData = ROW_DEFS.some(r => parseFloat(mData[r.key]) > 0);
          return (
            <div key={m.key} style={{ border: `1px solid ${isOpen ? C.navy : C.border}`, borderRadius: 10, background: C.white, minWidth: isOpen ? '100%' : 'auto', flex: isOpen ? '1 1 100%' : '0 0 auto' }}>
              <button onClick={() => setOpen(isOpen ? null : m.key)} style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '0.5rem 0.85rem', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, color: isOpen ? C.navy : C.ink }}>
                {hasData && !isOpen && <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.green, flexShrink: 0 }} />}
                {m.fullLabel}
                {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              </button>
              {isOpen && (
                <div style={{ padding: '0.75rem 0.85rem 0.85rem', borderTop: `1px solid ${C.border}` }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1rem' }}>
                    {ROW_DEFS.map(row => (
                      <div key={row.key}>
                        <div style={{ fontSize: '0.7rem', color: C.mid, marginBottom: 3, fontWeight: 600 }}>
                          {row.label}
                          {row.hitDay && <span style={{ fontSize: '0.62rem', color: C.faint, marginLeft: 4 }}>{row.hitDay}th</span>}
                        </div>
                        <NumInput value={mData[row.key] || 0} onChange={v => setCell(m.key, row.key, v)} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── PDF Export ────────────────────────────────────────────────────────────────
function exportToPDF(scenarioName, months, effectiveData, openingBalance, endingBalances, calcMonths, weeklySeries, now) {
  const win = window.open('', '_blank');
  if (!win) return;

  const fmtN = n => n == null || isNaN(n) ? '—' : new Intl.NumberFormat('nl-NL').format(Math.abs(Math.round(n)));

  // Build table HTML
  const thead = `<tr><th>Box Plantage</th>${months.map(m => `<th>${m.label}</th>`).join('')}</tr>`;
  const rows = ROW_DEFS.map(row => {
    const cells = months.map(m => {
      const fullVal = parseFloat(effectiveData[m.key]?.[row.key]) || 0;
      const dispVal = displayValue(fullVal, m, row.proRate);
      return `<td>${dispVal === 0 ? '—' : fmtN(dispVal)}</td>`;
    }).join('');
    return `<tr><td class="label">${row.label}${row.hitDay ? ` <span class="sub">${row.hitDay}th</span>` : ''}</td>${cells}</tr>`;
  }).join('');
  const movRow = `<tr class="movement"><td class="label">Movement +/−</td>${calcMonths.map(m => `<td class="${m.movement >= 0 ? 'pos' : 'neg'}">${m.movement >= 0 ? fmtN(m.movement) : `(${fmtN(Math.abs(m.movement))})`}</td>`).join('')}</tr>`;
  const balRow = `<tr class="ending"><td class="label">Ending Balance</td>${endingBalances.map(b => `<td class="${b >= 0 ? 'pos' : 'neg'}">${b < 0 ? `(${fmtN(Math.abs(b))})` : fmtN(b)}</td>`).join('')}</tr>`;

  // Build SVG chart inline
  const W = 740, H = 175, padL = 56, padR = 16, padT = 16, padB = 38;
  const chartW = W - padL - padR, chartH = H - padT - padB;
  const values = weeklySeries.map(s => s.balance);
  const minV = Math.min(...values, 0), maxV = Math.max(...values, 0);
  const vRange = (maxV - minV) || 1;
  const vMin = minV - vRange * 0.15, vMax = maxV + vRange * 0.15, vSpan = vMax - vMin;
  const toX = i => padL + (i / (weeklySeries.length - 1)) * chartW;
  const toY = v => padT + ((vMax - v) / vSpan) * chartH;
  const zeroY = toY(0);
  // Smooth bezier for PDF chart
  const pdfPts = weeklySeries.map((s, i) => ({ x: toX(i), y: toY(s.balance), balance: s.balance }));
  const linePath = 'M ' + pdfPts.map(p => p.x + ',' + p.y).join(' L ');
  const rawStep = (vMax - vMin) / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(Math.abs(rawStep) || 1)));
  const niceStep = Math.ceil(rawStep / mag) * mag;
  const yTicks = [];
  for (let v = Math.ceil(vMin / niceStep) * niceStep; v <= vMax + niceStep * 0.1; v += niceStep) yTicks.push(Math.round(v));
  const showEvery = weeklySeries.length > 20 ? 3 : weeklySeries.length > 10 ? 2 : 1;
  const pdfBuildFill = (above) => {
    const segs = []; let seg = null;
    for (let i = 0; i < pdfPts.length; i++) {
      const p = pdfPts[i], inZ = above ? p.balance >= 0 : p.balance < 0;
      if (inZ) {
        if (!seg) { const prev = pdfPts[i-1]; const ex = prev ? prev.x+((0-prev.balance)/(p.balance-prev.balance))*(p.x-prev.x) : p.x; seg = [{x:ex,y:zeroY},p]; }
        else seg.push(p);
      } else if (seg) {
        const prev = pdfPts[i-1]; const ex = prev ? prev.x+((0-prev.balance)/(p.balance-prev.balance))*(p.x-prev.x) : prev.x;
        seg.push({x:ex,y:zeroY}); segs.push([...seg]); seg = null;
      }
    }
    if (seg) { seg.push({x:pdfPts[pdfPts.length-1].x,y:zeroY}); segs.push(seg); }
    return segs.map(s => '<path d="M ' + s.map(p => p.x+','+p.y).join(' L ') + ' Z" fill="'+(above?'#7EA594':'#C0424F')+'" opacity="0.35"/>').join('');
  };
  const pdfFills = pdfBuildFill(true) + pdfBuildFill(false);

  const chartSVG = '<svg viewBox="0 0 ' + W + ' ' + H + '" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:auto">'
    + yTicks.map(v => '<line x1="' + padL + '" x2="' + (W-padR) + '" y1="' + toY(v) + '" y2="' + toY(v) + '" stroke="' + (v===0?'#2F3A42':'#D8DEE3') + '" stroke-width="' + (v===0?1.5:0.5) + '" stroke-dasharray="' + (v===0?'':' 3 4') + '" opacity="' + (v===0?1:0.5) + '"/><text x="' + (padL-4) + '" y="' + (toY(v)+3) + '" font-size="8" fill="#7A8A96" text-anchor="end" font-weight="' + (v===0?700:400) + '">' + fmtAxis(v) + '</text>').join('')
    + weeklySeries.map((s, i) => i > 0 && s.monthLabel ? '<line x1="' + toX(i) + '" x2="' + toX(i) + '" y1="' + padT + '" y2="' + (padT+chartH) + '" stroke="#C4CDD3" stroke-width="1" stroke-dasharray="3 3"/><text x="' + (toX(i)+3) + '" y="' + (padT+9) + '" font-size="8" fill="#7A8A96" font-weight="700">' + s.monthLabel + '</text>' : '').join('')
    + pdfFills
    + '<line x1="' + padL + '" x2="' + (W-padR) + '" y1="' + zeroY + '" y2="' + zeroY + '" stroke="#2F3A42" stroke-width="1.5"/>'
    + '<path d="' + linePath + '" fill="none" stroke="#445366" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>'
    + weeklySeries.map((s, i) => i % showEvery !== 0 ? '' : '<text x="' + toX(i) + '" y="' + (H-padB+13) + '" font-size="7.5" fill="#7A8A96" text-anchor="middle" transform="rotate(-35,' + toX(i) + ',' + (H-padB+13) + ')">' + s.weekLabel + '</text>').join('')
    + '</svg>';

  const yearEnd = endingBalances[endingBalances.length - 1];

  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
  <title> </title>
  <style>
    @page { size: A4 landscape; margin: 8mm 10mm; }
    @page :first { margin-top: 8mm; }
    html { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    /* Chrome suppresses URL/title when these are set: */
    head title { display: none; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #2F3A42; background: white; font-size: 10px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; padding-bottom: 7px; border-bottom: 2px solid #D8DEE3; }
    .header-left h1 { font-size: 14px; font-weight: 900; letter-spacing: -0.02em; margin-bottom: 2px; }
    .header-left .sub { font-size: 9px; color: #7A8A96; }
    .header-right { text-align: right; }
    .badge { display: inline-block; background: #C8E6D8; color: #2D7A65; border: 1px solid #7EA594; border-radius: 5px; padding: 1px 7px; font-size: 8px; font-weight: 700; margin-bottom: 3px; }
    .scenario-name { font-size: 11px; font-weight: 800; color: #E07B00; margin-bottom: 1px; }
    .stats { display: flex; gap: 7px; margin-bottom: 8px; }
    .stat { flex: 1; background: #F7F6EF; border: 1px solid #D8DEE3; border-radius: 6px; padding: 5px 8px; }
    .stat-label { font-size: 7px; font-weight: 700; color: #7A8A96; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 2px; }
    .stat-val { font-size: 12px; font-weight: 900; letter-spacing: -0.02em; }
    .pos { color: #7EA594; } .neg { color: #C0424F; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 8px; font-size: 8.5px; }
    th { background: #F7F6EF; padding: 3px 5px; text-align: right; font-weight: 800; border-bottom: 2px solid #D8DEE3; border-left: 1px solid #D8DEE3; color: #2F3A42; }
    th:first-child { text-align: left; border-left: none; font-size: 7px; text-transform: uppercase; letter-spacing: 0.06em; color: #7A8A96; }
    td { padding: 2.5px 5px; text-align: right; border-bottom: 1px solid #D8DEE3; border-left: 1px solid #D8DEE3; }
    td.label { text-align: left; font-weight: 500; border-left: none; }
    td .sub { font-size: 7px; color: #A8B5BD; margin-left: 3px; }
    tr.movement td { font-weight: 800; background: #F7F6EF; border-top: 2px solid #C4CDD3; }
    tr.ending td { font-weight: 900; font-size: 9px; background: #F7F6EF; }
    tr.movement td.pos, tr.ending td.pos { background: #EAF2EF; }
    tr.movement td.neg, tr.ending td.neg { background: #FFF0F0; }
    .chart-box { border: 1px solid #D8DEE3; border-radius: 7px; padding: 7px 10px; margin-bottom: 7px; }
    .chart-title { font-size: 9px; font-weight: 800; margin-bottom: 1px; }
    .chart-sub { font-size: 7px; color: #7A8A96; margin-bottom: 4px; }
    .footer { display: flex; justify-content: space-between; font-size: 7px; color: #A8B5BD; border-top: 1px solid #D8DEE3; padding-top: 6px; margin-top: 2px; }
  </style>
  </head><body>
  <div class="header">
    <div class="header-left">
      <div class="badge">Box Plantage</div>
      <div class="scenario-name">${scenarioName || 'Base Case'}</div>
    </div>
    <div class="header-right">
      <div style="font-size:9px;color:#7A8A96;">${now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
    </div>
  </div>
  <div class="stats">
    <div class="stat"><div class="stat-label">Opening Balance</div><div class="stat-val ${openingBalance >= 0 ? 'pos' : 'neg'}">${fmtEuro(openingBalance)}</div></div>
    <div class="stat"><div class="stat-label">Projected Year-End</div><div class="stat-val ${yearEnd >= 0 ? 'pos' : 'neg'}">${fmtEuro(yearEnd)}</div></div>
    <div class="stat"><div class="stat-label">Worst Month Balance</div><div class="stat-val neg">${fmtEuro(Math.min(...endingBalances))}</div></div>
    <div class="stat"><div class="stat-label">Best Month Balance</div><div class="stat-val pos">${fmtEuro(Math.max(...endingBalances))}</div></div>
  </div>
  <table><thead>${thead}</thead><tbody>${rows}${movRow}${balRow}</tbody></table>
  <div class="chart-box">
    <div class="chart-title">Weekly cash balance projection</div>
    <div class="chart-sub">Revenue &amp; costs spread daily · Rent hits 2nd · Payroll 28th · COVID/VAT/Disputes 29th</div>
    ${chartSVG}
  </div>

  <script>window.onload = () => window.print();</script>
  </body></html>`);
  win.document.close();
}

// ── Main component ────────────────────────────────────────────────────────────
export default function CashForecastTab() {
  const months = getForecastMonths();
  const now = new Date();

  const [data, setData] = useState({});
  const [openingBalance, setOpeningBalance] = useState(0);
  const [editingBalance, setEditingBalance] = useState(false);
  const [rawBalance, setRawBalance] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [loaded, setLoaded] = useState(false);

  // Scenario state
  const [scenarioName, setScenarioName] = useState('Base Case');
  const [revPct, setRevPct] = useState(0);
  const [overrides, setOverrides] = useState(() => Object.fromEntries(ROW_DEFS.map(r => [r.key, null])));

  useEffect(() => {
    async function load() {
      const { data: rows, error } = await supabase.from('cash_forecast').select('month, data').eq('entity', ENTITY);
      if (error) { console.error(error); setLoaded(true); return; }
      const merged = {};
      rows.forEach(r => { merged[r.month] = r.data; });
      if (merged['__meta__']?.opening_balance != null) setOpeningBalance(parseFloat(merged['__meta__'].opening_balance) || 0);
      setData(merged);
      setLoaded(true);
    }
    load();
  }, []);

  const setCell = useCallback((monthKey, rowKey, value) => {
    setData(prev => ({ ...prev, [monthKey]: { ...(prev[monthKey] || {}), [rowKey]: value } }));
  }, []);

  async function saveAll() {
    setSaving(true); setSaveError(null);
    try {
      const upserts = Object.entries(data).filter(([m]) => m !== '__meta__').map(([month, mData]) => ({ entity: ENTITY, month, data: mData, updated_at: new Date().toISOString() }));
      upserts.push({ entity: ENTITY, month: '__meta__', data: { opening_balance: openingBalance }, updated_at: new Date().toISOString() });
      const { error } = await supabase.from('cash_forecast').upsert(upserts, { onConflict: 'entity,month' });
      if (error) throw error;
    } catch (e) { setSaveError(e.message); }
    finally { setSaving(false); }
  }

  function handleReset() {
    setRevPct(0);
    setOverrides(Object.fromEntries(ROW_DEFS.map(r => [r.key, null])));
    setScenarioName('Base Case');
  }

  // Apply scenario to get effective data
  const effectiveData = (revPct !== 0 || Object.values(overrides).some(v => v !== null && v !== ''))
    ? applyScenario(data, months, revPct, overrides)
    : data;

  // Calculations on effective data
  const calcMonths = months.map(mObj => {
    const mData = effectiveData[mObj.key] || {};
    const getValue = (rowKey, proRate) => displayValue(parseFloat(mData[rowKey]) || 0, mObj, proRate);
    const inflow = ROW_DEFS.filter(r => r.sign === 1).reduce((s, r) => s + getValue(r.key, r.proRate), 0);
    const outflow = ROW_DEFS.filter(r => r.sign === -1).reduce((s, r) => s + getValue(r.key, r.proRate), 0);
    return { ...mObj, movement: inflow - outflow };
  });

  let runBal = openingBalance;
  const endingBalances = calcMonths.map(m => { runBal += m.movement; return Math.round(runBal); });

  const weeklySeries = loaded ? buildWeeklySeries(months, effectiveData, openingBalance) : [];

  const COL_W = 96, LABEL_W = 190;
  const isCurrentMonth = m => now.getMonth() === m.month && now.getFullYear() === m.year;

  function handleExport() {
    exportToPDF(scenarioName, months, effectiveData, openingBalance, endingBalances, calcMonths, weeklySeries, now);
  }

  if (!loaded) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3rem', color: C.mid }}>
      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
      <span style={{ fontSize: '0.85rem' }}>Loading forecast…</span>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ background: ENTITY_STYLE.bg, color: ENTITY_STYLE.text, border: `1px solid ${ENTITY_STYLE.border}`, borderRadius: 8, padding: '0.3rem 0.85rem', fontSize: '0.8rem', fontWeight: 700 }}>{ENTITY}</div>
          <div style={{ fontSize: '0.78rem', color: C.mid }}>Cash flow forecast · {now.getFullYear()}</div>
        </div>
        <button onClick={saveAll} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6, background: C.navy, color: C.white, border: 'none', borderRadius: 8, padding: '0.45rem 1rem', fontSize: '0.8rem', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          <Save size={13} />{saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {saveError && <div style={{ background: C.redBg, border: `1px solid ${C.red}`, borderRadius: 8, padding: '0.65rem 1rem', color: C.red, fontSize: '0.8rem', marginBottom: '1rem', display: 'flex', gap: 6, alignItems: 'center' }}><AlertCircle size={14} />{saveError}</div>}

      {/* Balance banner */}
      <div style={{ background: openingBalance < 0 ? C.redBg : C.greenBg, border: `1.5px solid ${openingBalance < 0 ? C.red : C.green}`, borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Current Bank Balance</div>
          {editingBalance ? (
            <input value={rawBalance} onChange={e => setRawBalance(e.target.value)}
              onBlur={() => { const v = parseFloat(rawBalance.replace(/[^0-9.-]/g, '')); setOpeningBalance(isNaN(v) ? 0 : v); setEditingBalance(false); }}
              onKeyDown={e => { if (e.key === 'Enter') { const v = parseFloat(rawBalance.replace(/[^0-9.-]/g, '')); setOpeningBalance(isNaN(v) ? 0 : v); setEditingBalance(false); } if (e.key === 'Escape') setEditingBalance(false); }}
              autoFocus style={{ fontSize: '1.6rem', fontWeight: 900, border: `2px solid ${C.navy}`, borderRadius: 6, padding: '2px 8px', width: 180, color: C.ink, background: C.white, outline: 'none' }} />
          ) : (
            <div onClick={() => { setRawBalance(String(openingBalance)); setEditingBalance(true); }}
              style={{ fontSize: '1.6rem', fontWeight: 900, color: openingBalance < 0 ? C.red : C.green, cursor: 'pointer', letterSpacing: '-0.03em' }} title="Click to edit">
              {fmtEuro(openingBalance)}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: C.mid }}>
          <TrendingDown size={14} />
          Projected year-end:
          <strong style={{ color: endingBalances[endingBalances.length - 1] < 0 ? C.red : C.green, marginLeft: 4 }}>
            {fmtEuro(endingBalances[endingBalances.length - 1])}
          </strong>
        </div>
      </div>

      {/* Scenario panel */}
      <ScenarioPanel
        scenarioName={scenarioName} setScenarioName={setScenarioName}
        revPct={revPct} setRevPct={setRevPct}
        overrides={overrides} setOverrides={setOverrides}
        onReset={handleReset} onExport={handleExport}
      />

      {/* Forecast input panel */}
      <ForecastInputPanel months={months} data={data} setCell={setCell} />

      {/* Table */}
      <div style={{ overflowX: 'auto', borderRadius: 12, border: `1px solid ${C.border}`, background: C.white, marginBottom: '1.5rem' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: LABEL_W + COL_W * months.length }}>
          <thead>
            <tr style={{ background: C.rowAlt }}>
              <th style={{ width: LABEL_W, padding: '0.6rem 0.85rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${C.border}`, position: 'sticky', left: 0, background: C.rowAlt, zIndex: 2 }}>{ENTITY}</th>
              {months.map(m => (
                <th key={m.key} style={{ width: COL_W, padding: '0.6rem 0.5rem', textAlign: 'right', fontSize: '0.78rem', fontWeight: 800, color: isCurrentMonth(m) ? C.navy : C.ink, borderBottom: `2px solid ${C.border}`, borderLeft: `1px solid ${C.border}`, background: isCurrentMonth(m) ? C.navyLight : C.rowAlt }}>
                  {m.label}
                  {isCurrentMonth(m) && <div style={{ fontSize: '0.62rem', fontWeight: 500, color: C.skyText, marginTop: 1 }}>{daysRemaining(m)}d remaining</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROW_DEFS.map((row, rIdx) => (
              <tr key={row.key} style={{ borderBottom: `1px solid ${C.border}`, background: rIdx % 2 === 0 ? C.white : '#FAFAF7' }}>
                <td style={{ padding: '0 0 0 0.85rem', fontSize: '0.8rem', color: C.ink, fontWeight: 500, position: 'sticky', left: 0, background: rIdx % 2 === 0 ? C.white : '#FAFAF7', zIndex: 1, height: 36 }}>
                  {row.label}
                  {row.hitDay && <span style={{ fontSize: '0.62rem', color: C.faint, marginLeft: 5 }}>{row.hitDay}th</span>}
                  {REVENUE_LINKED.has(row.key) && revPct !== 0 && <span style={{ fontSize: '0.62rem', color: C.amber, marginLeft: 5 }}>{revPct > 0 ? '+' : ''}{revPct}%</span>}
                </td>
                {months.map(m => {
                  const fullVal = parseFloat(effectiveData[m.key]?.[row.key]) || 0;
                  const baseVal = parseFloat(data[m.key]?.[row.key]) || 0;
                  const dispVal = displayValue(fullVal, m, row.proRate);
                  const isModified = fullVal !== baseVal;
                  return (
                    <td key={m.key} style={{ padding: '4px 10px', borderLeft: `1px solid ${C.border}`, height: 36, verticalAlign: 'middle', textAlign: 'right', background: isModified ? C.amberBg : 'transparent' }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 500, color: dispVal === 0 ? C.faint : isModified ? C.amber : C.ink }}>
                        {dispVal === 0 ? '—' : fmt(dispVal)}
                      </span>
                      {isCurrentMonth(m) && fullVal > 0 && !isModified && <div style={{ fontSize: '0.62rem', color: C.faint }}>of {fmt(fullVal)}</div>}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr style={{ borderTop: `2px solid ${C.borderMid}`, background: C.rowAlt }}>
              <td style={{ padding: '0 0 0 0.85rem', fontSize: '0.8rem', fontWeight: 800, color: C.ink, position: 'sticky', left: 0, background: C.rowAlt, zIndex: 1, height: 38 }}>Movement +/−</td>
              {calcMonths.map(m => (
                <td key={m.key} style={{ padding: '2px 6px', borderLeft: `1px solid ${C.border}`, height: 38, verticalAlign: 'middle' }}>
                  <div style={{ textAlign: 'right', padding: '4px 6px', fontSize: '0.8rem', fontWeight: 800, color: m.movement >= 0 ? C.green : C.red, background: m.movement >= 0 ? C.greenBg : C.redBg, borderRadius: 4 }}>
                    {m.movement >= 0 ? fmt(m.movement) : `(${fmt(Math.abs(m.movement))})`}
                  </div>
                </td>
              ))}
            </tr>
            <tr style={{ borderTop: `1px solid ${C.border}`, background: C.rowAlt }}>
              <td style={{ padding: '0 0 0 0.85rem', fontSize: '0.82rem', fontWeight: 900, color: C.ink, position: 'sticky', left: 0, background: C.rowAlt, zIndex: 1, height: 42 }}>Ending Balance</td>
              {endingBalances.map((bal, idx) => (
                <td key={months[idx].key} style={{ padding: '2px 6px', borderLeft: `1px solid ${C.border}`, height: 42, verticalAlign: 'middle' }}>
                  <div style={{ textAlign: 'right', padding: '5px 6px', fontSize: '0.82rem', fontWeight: 900, color: bal < 0 ? C.red : C.green, background: bal < 0 ? C.redBg : C.greenBg, borderRadius: 6, border: `1px solid ${bal < 0 ? '#F5C6CB' : '#C3E6CB'}` }}>
                    {bal < 0 ? `(${fmt(Math.abs(bal))})` : fmt(bal)}
                  </div>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Weekly chart */}
      {weeklySeries.length > 0 && (
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: '1.25rem' }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 800, color: C.ink, marginBottom: 2 }}>Weekly cash balance projection</div>
          <div style={{ fontSize: '0.72rem', color: C.mid, marginBottom: '0.5rem' }}>Revenue & costs spread daily · Rent 2nd · Payroll 28th · COVID/VAT/Disputes 29th · Hover for detail</div>
          <WeeklyChart series={weeklySeries} />
          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            {[{ color: C.green, label: 'Above zero' }, { color: C.red, label: 'Below zero' }].map(l => (
              <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.7rem', color: C.mid }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: l.color }} />{l.label}
              </div>
            ))}
            <div style={{ fontSize: '0.7rem', color: C.faint, marginLeft: 'auto' }}>Dashed lines = month boundaries</div>
          </div>
        </div>
      )}

      <div style={{ fontSize: '0.7rem', color: C.faint, marginTop: '1rem', textAlign: 'right' }}>
        Current month prorated · {daysRemaining(months[0])} days remaining · Save to persist base forecast
      </div>
    </div>
  );
}